import { useCallback, useEffect, useState } from "react";
import { Loader2, UserPlus, Users } from "lucide-react";
import { Navigate, useParams } from "react-router-dom";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { pageActionButtonClassName, pageActionLabelClassName } from "@/lib/page-action-button";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/contexts/ToastContext";
import { getWorkspace, removeWorkspaceMember } from "../api/rag-api.js";
import { mapWorkspace, mapWorkspaceUserToMember } from "../api/mappers.js";
import {
  AddWorkspaceMembersDialog,
  type AddedWorkspaceMember,
} from "../components/AddWorkspaceMembersDialog.js";
import { RagMemberRow } from "../components/RagMemberRow.js";
import { RagPageHeader } from "../components/RagPageHeader.js";
import { RagPermissionNote } from "../components/RagPermissionNote.js";
import { canManageKmWorkspaceMembers } from "../lib/km-org-permissions.js";
import type { RagMember, RagWorkspace } from "../types.js";

function mergeAddedMembers(
  workspace: RagWorkspace,
  added: AddedWorkspaceMember[],
): RagMember[] {
  const membersById = new Map(workspace.members.map((member) => [member.id, member]));

  for (const entry of added) {
    const mapped = mapWorkspaceUserToMember(entry.user, entry.role, workspace.ownerId);
    if (mapped) {
      membersById.set(mapped.id, mapped);
    }
  }

  const owner = membersById.get(workspace.ownerId);
  const others = [...membersById.values()].filter((member) => member.id !== workspace.ownerId);

  return owner ? [owner, ...others] : others;
}

export function WorkspaceMembersPage() {
  const { workspaceId = "" } = useParams();
  const { user, activeOrganization, hasPermission } = useAuth();
  const { showToast } = useToast();
  const [workspace, setWorkspace] = useState<RagWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadError, setReloadError] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<RagMember | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const loadWorkspace = useCallback(async (isReload = false) => {
    if (!isReload) {
      setLoading(true);
      setError(null);
    }
    setReloadError(null);
    try {
      const { workspace: workspaceData } = await getWorkspace(workspaceId);
      setWorkspace(mapWorkspace(workspaceData));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load workspace";
      if (isReload) {
        setReloadError(message);
      } else {
        setError(message);
        setWorkspace(null);
      }
    } finally {
      if (!isReload) {
        setLoading(false);
      }
    }
  }, [workspaceId]);

  const handleMembersAdded = useCallback(
    async (added: AddedWorkspaceMember[]) => {
      if (added.length === 0) {
        return;
      }

      showToast(
        added.length === 1 ? "Added 1 member" : `Added ${added.length} members`,
        "success",
      );

      try {
        const { workspace: workspaceData } = await getWorkspace(workspaceId);
        const mapped = mapWorkspace(workspaceData);
        const members = mergeAddedMembers(mapped, added);
        setWorkspace({
          ...mapped,
          members,
          memberCount: members.length,
        });
        setReloadError(null);
      } catch (err) {
        setWorkspace((current) => {
          if (!current) {
            return current;
          }

          const members = mergeAddedMembers(current, added);
          return {
            ...current,
            members,
            memberCount: members.length,
          };
        });
        setReloadError(err instanceof Error ? err.message : "Failed to load workspace");
      }
    },
    [showToast, workspaceId],
  );

  const handleRemoveMember = useCallback(async () => {
    const member = removeTarget;
    if (!member) {
      return;
    }

    setRemovingId(member.id);
    try {
      await removeWorkspaceMember(workspaceId, member.id);
      setWorkspace((current) => {
        if (!current) {
          return current;
        }
        const members = current.members.filter((entry) => entry.id !== member.id);
        return {
          ...current,
          members,
          memberCount: members.length,
        };
      });
      showToast(`Removed ${member.name}`, "success");
      setRemoveTarget(null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to remove member", "error");
    } finally {
      setRemovingId(null);
    }
  }, [removeTarget, showToast, workspaceId]);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  if (!loading && (error || !workspace)) {
    return <Navigate to="/rag" replace />;
  }

  if (loading || !workspace) {
    return (
      <section className="flex flex-1 items-center justify-center p-6">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </section>
    );
  }

  const canManageMembers =
    Boolean(activeOrganization) &&
    canManageKmWorkspaceMembers(hasPermission, user?.id, workspace);

  return (
    <section className="flex flex-1 flex-col p-6 pb-24 sm:pb-6">
      <RagPageHeader
        icon={Users}
        title="Members"
        subtitle={`${workspace.name} · ${workspace.members.length} members`}
        mobileActions={
          canManageMembers
            ? [
                {
                  id: "add-member",
                  icon: UserPlus,
                  label: "Add member",
                  onClick: () => setAddDialogOpen(true),
                },
              ]
            : undefined
        }
        action={
          canManageMembers ? (
            <Button
              variant="accent"
              onClick={() => setAddDialogOpen(true)}
              title="Add member"
              aria-label="Add member"
              className={pageActionButtonClassName}
            >
              <UserPlus className="h-4 w-4" />
              <span className={pageActionLabelClassName}>Add member</span>
            </Button>
          ) : null
        }
      />
      <div className="flex flex-col gap-2">
        {reloadError ? (
          <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {reloadError}
          </p>
        ) : null}
        {!canManageMembers && activeOrganization ? (
          <RagPermissionNote variant="info">
            {workspace.isDefault
              ? "Only the workspace owner can add members to the default workspace."
              : "Only the workspace owner or a workspace admin can add members."}
          </RagPermissionNote>
        ) : null}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-3">
          {workspace.members.map((member) => (
            <RagMemberRow
              key={member.id}
              member={member}
              removing={removingId === member.id}
              onRemove={
                canManageMembers && member.id !== workspace.ownerId
                  ? setRemoveTarget
                  : undefined
              }
            />
          ))}
        </div>
        {!canManageMembers && !activeOrganization ? (
          <p className="mt-2 text-sm text-slate-400">
            Organization required to add members from your team.
          </p>
        ) : null}
      </div>

      <ConfirmDialog
        isOpen={removeTarget != null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemoveMember}
        title="Remove member"
        description={
          removeTarget
            ? `Remove ${removeTarget.name} from this workspace?`
            : ""
        }
        confirmLabel="Remove member"
        variant="destructive"
        isLoading={removingId != null}
      />
      <AddWorkspaceMembersDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        workspaceId={workspace.id}
        ownerId={workspace.ownerId}
        existingMemberIds={workspace.members.map((member) => member.id)}
        onAdded={handleMembersAdded}
      />
    </section>
  );
}
